import { lazy } from "react";
import { RequirePermission } from "../components/RequirePermission";
import { Navigate, Route } from "react-router";
import { StoreRedirect } from "../features/store/StoreRedirect";
import { PageLoading } from "../components/common";
import { useEntitlement } from "../features/billing/useEntitlement";
import { MARKETPLACE_SUBMIT_FEATURE } from "../features/workspace/nav-config";
import { useWorkspace } from "../features/workspace/WorkspaceContext";

const DeliveryReportPage = lazy(() =>
  import("../features/analytics/delivery/DeliveryReportPage").then((m) => ({
    default: m.DeliveryReportPage,
  })),
);
const EntryPointDashboard = lazy(() =>
  import("../features/analytics/entry-points/EntryPointDashboard").then((m) => ({
    default: m.EntryPointDashboard,
  })),
);
const AnalyticsOverviewPage = lazy(() =>
  import("../pages/analytics/overview").then((m) => ({ default: m.AnalyticsOverviewPage })),
);
const AnalyticsPlusPage = lazy(() =>
  import("../pages/analytics/plus").then((m) => ({ default: m.AnalyticsPlusPage })),
);
const PaymentsAnalyticsPage = lazy(() =>
  import("../pages/analytics/payments").then((m) => ({ default: m.PaymentsAnalyticsPage })),
);
const WinbackPage = lazy(() =>
  import("../features/sell/ai-outbound-queue/WinbackPage").then((m) => ({
    default: m.WinbackPage,
  })),
);
const BrowsePage = lazy(() =>
  import("../features/marketplace/browse/BrowsePage").then((m) => ({ default: m.BrowsePage })),
);
const SubmitListingPage = lazy(() =>
  import("../features/marketplace/submit-listing/SubmitListingPage").then((m) => ({
    default: m.SubmitListingPage,
  })),
);
function MarketplaceSubmitGate() {
  const { workspace } = useWorkspace();
  const state = useEntitlement(workspace.id, MARKETPLACE_SUBMIT_FEATURE);
  // OWN-373: unknown (still loading) waits; off sends the member to the marketplace.
  if (state === null) return <PageLoading />;
  return state.enabled ? <SubmitListingPage /> : <Navigate replace to=".." relative="path" />;
}
const StorePage = lazy(() =>
  import("../features/store/StorePage").then((m) => ({ default: m.StorePage })),
);
const StoreProductsTab = lazy(() =>
  import("../features/store/StoreProductsTab").then((m) => ({ default: m.StoreProductsTab })),
);
const StorePaymentsTab = lazy(() =>
  import("../features/store/StorePaymentsTab").then((m) => ({ default: m.StorePaymentsTab })),
);
const StoreSettingsTab = lazy(() =>
  import("../features/store/StoreSettingsTab").then((m) => ({ default: m.StoreSettingsTab })),
);
const CartsPage = lazy(() =>
  import("../features/carts/CartsPage").then((m) => ({ default: m.CartsPage })),
);
const QuotesPage = lazy(() =>
  import("../features/quotes/list/QuotesPage").then((m) => ({ default: m.QuotesPage })),
);
const QuoteSettingsPage = lazy(() =>
  import("../features/quotes/settings/QuoteSettingsPage").then((m) => ({
    default: m.QuoteSettingsPage,
  })),
);
const NewOrderPage = lazy(() =>
  import("../features/store/orders/NewOrderPage").then((m) => ({ default: m.NewOrderPage })),
);
const OrderPage = lazy(() =>
  import("../features/store/orders/OrderPage").then((m) => ({ default: m.OrderPage })),
);
const OrdersPage = lazy(() =>
  import("../features/store/orders/OrdersPage").then((m) => ({ default: m.OrdersPage })),
);
const OrderMessagesPage = lazy(() =>
  import("../features/store/order-messages/OrderMessagesPage").then((m) => ({
    default: m.OrderMessagesPage,
  })),
);
const PaymentQueuePage = lazy(() =>
  import("../features/store/payment-queue/PaymentQueuePage").then((m) => ({
    default: m.PaymentQueuePage,
  })),
);

// Route order inside <Route path="/w/:workspaceId"> is preserved: this fragment slots in where it was inline.
export const workspaceStoreRoutes = (
  <>
    <Route
      path="analytics"
      element={
        <RequirePermission anyOf={["analytics.view"]}>
          <AnalyticsOverviewPage />
        </RequirePermission>
      }
    />
    <Route
      path="analytics/delivery"
      element={
        <RequirePermission anyOf={["analytics.view"]}>
          <DeliveryReportPage />
        </RequirePermission>
      }
    />
    <Route
      path="analytics/entry-points"
      element={
        <RequirePermission anyOf={["analytics.view"]}>
          <EntryPointDashboard />
        </RequirePermission>
      }
    />
    <Route
      path="analytics/sales"
      element={
        <RequirePermission anyOf={["analytics.view"]}>
          <AnalyticsPlusPage />
        </RequirePermission>
      }
    />
    <Route
      path="analytics/payments"
      element={
        <RequirePermission anyOf={["analytics.view"]}>
          <PaymentsAnalyticsPage />
        </RequirePermission>
      }
    />
    <Route path="sell" element={<Navigate replace to="../getting-started" relative="path" />} />
    <Route path="sell/winback" element={<WinbackPage />} />
    <Route path="catalog" element={<StoreRedirect to="store/settings" />} />
    <Route path="store/connections" element={<StoreRedirect to="store/settings" />} />
    <Route path="store/catalog" element={<StoreRedirect to="store/settings" />} />
    <Route
      path="store"
      element={
        <RequirePermission anyOf={["commerce.view"]} module="store">
          <StorePage />
        </RequirePermission>
      }
    >
      <Route index element={<StoreRedirect to="store/orders" />} />
      <Route path="orders" element={<OrdersPage />} />
      <Route
        path="products"
        element={
          <RequirePermission anyOf={["commerce.view"]} module="products">
            <StoreProductsTab />
          </RequirePermission>
        }
      />
      <Route path="carts" element={<CartsPage />} />
      <Route path="payments" element={<StorePaymentsTab />} />
      <Route
        path="quotes"
        element={
          <RequirePermission anyOf={["commerce.view"]} module="quotes">
            <QuotesPage />
          </RequirePermission>
        }
      />
      <Route
        path="quotes/settings"
        element={
          <RequirePermission anyOf={["commerce.view"]} module="quotes">
            <QuoteSettingsPage />
          </RequirePermission>
        }
      />
      <Route path="settings" element={<StoreSettingsTab />} />
    </Route>
    <Route
      path="store/orders/messages"
      element={
        <RequirePermission anyOf={["commerce.view"]} module="store">
          <OrderMessagesPage />
        </RequirePermission>
      }
    />
    <Route
      path="store/orders/payments"
      element={
        <RequirePermission anyOf={["commerce.view"]} module="store">
          <PaymentQueuePage />
        </RequirePermission>
      }
    />
    <Route
      path="store/orders/new"
      element={
        <RequirePermission anyOf={["commerce.view"]} module="store">
          <NewOrderPage />
        </RequirePermission>
      }
    />
    <Route
      path="store/orders/:orderId"
      element={
        <RequirePermission anyOf={["commerce.view"]} module="store">
          <OrderPage />
        </RequirePermission>
      }
    />
    <Route path="marketplace" element={<BrowsePage />} />
    <Route path="marketplace/submit" element={<MarketplaceSubmitGate />} />
  </>
);
